import { useContext, useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import moment from 'moment';

import Meta from "../../components/Meta";
import Loading from "../../components/Loading";

import Data from '../../../repository';
import AppContext from "../../../context";

import styles from '../../../styles/Entry.module.scss';

const radioStyle = { width: 'auto' };

function QuestionRow({ index, question, answer, onChange }) {
	return (
		<div style={{ padding: '12px 0', borderBottom: '1px solid #eee' }}>
			<p style={{ marginBottom: '10px' }}>{index}. {question}</p>
			<div style={{ display: 'flex', gap: '30px' }}>
				<label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'normal' }}>
					<input type="radio" style={radioStyle} checked={answer === 'yes'} onChange={() => onChange('yes')} />
					Yes
				</label>
				<label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'normal' }}>
					<input type="radio" style={radioStyle} checked={answer === 'no'} onChange={() => onChange('no')} />
					No
				</label>
			</div>
		</div>
	);
}

function HealthScreening() {
	const context = useContext(AppContext);
	const signatureRef = useRef(null);

	const [loading, setLoading] = useState(false);
	const [questionsLoading, setQuestionsLoading] = useState(true);
	const [questionsError, setQuestionsError] = useState(null);
	const [serverError, setServerError] = useState(null);
	const [signatureError, setSignatureError] = useState(null);
	const [result, setResult] = useState(null);

	const [personal, setPersonal] = useState({
		fullName: context.user ? `${context.user.firstName || ''} ${context.user.lastName || ''}`.trim() : '',
		dateOfBirth: '',
		phoneNumber: context.user?.phoneNumber || '',
		email: context.user?.email || '',
		emergencyContactName: '',
		emergencyContactRelationship: '',
		emergencyContactPhone: ''
	});

	const [section2, setSection2] = useState([]);
	const [section3, setSection3] = useState([]);
	const [protocolAgreed, setProtocolAgreed] = useState(false);
	const [declarationAgreed, setDeclarationAgreed] = useState(false);

	useEffect(() => {
		const loadQuestions = async () => {
			setQuestionsLoading(true);
			setQuestionsError(null);
			try {
				const response = await Data.fetchHealthScreeningQuestions();
				setSection2(response.data.section2.map((q) => ({ _id: q._id, question: q.question, answer: null })));
				setSection3(response.data.section3.map((q) => ({ _id: q._id, question: q.question, answer: null })));
			} catch (error) {
				setQuestionsError('Unable to load the health screening questions. Please try again.');
			}
			setQuestionsLoading(false);
		};
		loadQuestions();
	}, []);

	const updatePersonal = (key, value) => setPersonal((prev) => ({ ...prev, [key]: value }));

	const updateAnswer = (list, setList, index, answer) => {
		setList(list.map((q, i) => i === index ? { ...q, answer } : q));
	};

	const allAnswered = section2.length > 0 && section3.length > 0
		&& section2.every((q) => q.answer) && section3.every((q) => q.answer);

	const canSubmit = personal.fullName && personal.dateOfBirth && personal.phoneNumber && personal.email
		&& personal.emergencyContactName && personal.emergencyContactRelationship && personal.emergencyContactPhone
		&& allAnswered && protocolAgreed && declarationAgreed;

	const onSubmit = async (event) => {
		event.preventDefault();
		setServerError(null);
		setSignatureError(null);

		if (!canSubmit) {
			setServerError('Please complete every field, answer every question, and agree to the protocol and declaration before continuing.');
			return;
		}

		if (!signatureRef.current || signatureRef.current.isEmpty()) {
			setSignatureError('Please sign the form to confirm your declaration.');
			return;
		}

		setLoading(true);

		try {
			const signatureImage = signatureRef.current.toDataURL('image/png');
			const signatureDate = moment().format('DD/MM/YYYY');

			const response = await Data.submitHealthScreening({
				personal,
				answers: {
					section2: section2.map(({ _id, answer }) => ({ questionId: _id, answer })),
					section3: section3.map(({ _id, answer }) => ({ questionId: _id, answer }))
				},
				protocolAgreed,
				declarationAgreed,
				signatureDate,
				signatureImage
			});

			await context.fetchUser();

			if (response.data?.hasConcerns) {
				setResult('concerns');
				setLoading(false);
			} else {
				window.location.href = '/';
			}
		} catch (error) {
			setServerError(error.response?.data?.message || 'An error occurred. Please try again.');
			setLoading(false);
		}
	};

	if (questionsLoading) {
		return (
			<>
				<Meta title="Health Screening" />
				<div className={styles.fullHeight}>
					<Loading button={false} />
				</div>
			</>
		);
	}

	if (questionsError) {
		return (
			<>
				<Meta title="Health Screening" />
				<div className={styles.fullHeight}>
					<h1>Health Screening</h1>
					<p style={{ marginTop: '20px' }}>{questionsError}</p>
				</div>
			</>
		);
	}

	if (result === 'concerns') {
		return (
			<>
				<Meta title="Health Screening" />
				<div className={styles.fullHeight}>
					<h1>Thanks for completing your health screening</h1>
					<p style={{ marginTop: '20px' }}>
						One or more of your answers means we need to check in with you before you can make a
						booking. We've emailed the business with your submission - please contact them directly
						so they can confirm you're OK to exercise. Once they've reviewed your answers, you'll be
						able to book as normal.
					</p>
					<div className={styles.actions}>
						<button type="button" className="btn btnPrimary" onClick={() => window.location.href = '/'}>
							Continue to your account
						</button>
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<Meta title="Health Screening" />

			<div className={styles.fullHeight}>
				<h1>Physical Activity Readiness & Lone Training Screening Questionnaire (PAR-Q+)</h1>
				<p style={{ marginTop: '10px', opacity: 0.7 }}>
					This form complies with UK Health and Safety standards, CIMSPA industry guidelines, and the
					UK Data Protection Act 2018 / UK GDPR for processing special category health data.
				</p>

				<form onSubmit={onSubmit} className={styles.formScroll}>
					<h2>Section 1: Client Personal Details</h2>
					<div className={styles.form}>
						<div>
							<p>Full Name</p>
							<input type="text" value={personal.fullName} onChange={(e) => updatePersonal('fullName', e.target.value)} />
						</div>
						<div>
							<p>Date of Birth</p>
							<input type="date" value={personal.dateOfBirth} onChange={(e) => updatePersonal('dateOfBirth', e.target.value)} />
						</div>
						<div>
							<p>Phone Number</p>
							<input type="tel" value={personal.phoneNumber} onChange={(e) => updatePersonal('phoneNumber', e.target.value)} />
						</div>
						<div>
							<p>Email Address</p>
							<input type="email" value={personal.email} onChange={(e) => updatePersonal('email', e.target.value)} />
						</div>
						<div>
							<p>Emergency Contact Name</p>
							<input type="text" value={personal.emergencyContactName} onChange={(e) => updatePersonal('emergencyContactName', e.target.value)} />
						</div>
						<div>
							<p>Emergency Contact Relationship</p>
							<input type="text" value={personal.emergencyContactRelationship} onChange={(e) => updatePersonal('emergencyContactRelationship', e.target.value)} />
						</div>
						<div>
							<p>Emergency Contact Phone</p>
							<input type="tel" value={personal.emergencyContactPhone} onChange={(e) => updatePersonal('emergencyContactPhone', e.target.value)} />
						</div>
					</div>

					<h2 style={{ marginTop: '30px' }}>Section 2: Standard Health Readiness (PAR-Q+)</h2>
					<p style={{ opacity: 0.7 }}>
						Please answer the health questions below. If you are between the ages of 15 and
						69, the PAR-Q+ will tell you if you should check with your doctor before you significantly
						increase your physical activity.
					</p>
					{section2.map((q, i) => (
						<QuestionRow key={q._id} index={i + 1} question={q.question} answer={q.answer} onChange={(answer) => updateAnswer(section2, setSection2, i, answer)} />
					))}

					<h2 style={{ marginTop: '30px' }}>Section 3: Solo / Unsupervised Training Risk Assessment</h2>
					<p style={{ opacity: 0.7 }}>
						Because your exercise sessions may take place alone and unsupervised, additional safety
						precautions are required by UK duty-of-care standards to manage risks associated with
						sudden medical incidents or physical incapacity.
					</p>
					{section3.map((q, i) => (
						<QuestionRow key={q._id} index={i + 1} question={q.question} answer={q.answer} onChange={(answer) => updateAnswer(section3, setSection3, i, answer)} />
					))}

					<h2 style={{ marginTop: '30px' }}>Section 4: Solo Training Safety Protocol Agreement</h2>
					<p style={{ opacity: 0.7 }}>If approved to exercise alone, you agree to adhere strictly to the following safety protocols during every solo session:</p>
					<ul style={{ margin: '10px 0', paddingLeft: '20px', opacity: 0.7, lineHeight: 1.6 }}>
						<li>You will notify your named emergency contact or a responsible adult of your location, start time, and estimated completion time prior to commencing solo training.</li>
						<li>You will carry a fully charged mobile device or smart wearable equipped with emergency SOS / fall detection capability at all times.</li>
						<li>If you carry medication (e.g., inhalers, glucose gel, EpiPen), it will be clearly accessible and on your person or in your immediate training area.</li>
						<li>When training alone with heavy weights, you agree not to perform maximum-load movements without mechanical fail-safes (e.g., using a power rack with safety spotter arms or machine equivalents instead of unspotted barbell bench pressing/squatting).</li>
						<li>You agree to immediately cease exercising if you feel lightheaded, faint, abnormally short of breath, or experience severe pain.</li>
					</ul>
					<label className={styles.checkbox}>
						<input type="checkbox" style={radioStyle} checked={protocolAgreed} onChange={(e) => setProtocolAgreed(e.target.checked)} />
						I have read and agree to follow the Solo Training Safety Protocol Agreement above.
					</label>

					<h2 style={{ marginTop: '30px' }}>Section 6: UK GDPR Consent & Client Declaration</h2>
					<p style={{ opacity: 0.7 }}>
						The personal and health information collected on this form is classified as Special
						Category Data under the UK Data Protection Act 2018 and UK GDPR. It is processed solely
						for the purpose of assessing your fitness readiness, ensuring safe physical training
						conditions, and providing emergency information if required. Your data will be stored
						securely, kept strictly confidential, and will not be shared with third parties without
						your explicit consent unless required by law or in a life-threatening emergency.
					</p>
					<p style={{ opacity: 0.7, marginTop: '10px' }}>
						I confirm that I have read, understood, and accurately completed this questionnaire; that
						I acknowledge the inherent risks of physical activity (including delayed assistance in an
						emergency during solo training); and that I will notify my provider immediately of any
						changes to my medical status, health conditions, or contact details.
					</p>
					<label className={styles.checkbox} style={{ marginTop: '10px' }}>
						<input type="checkbox" style={radioStyle} checked={declarationAgreed} onChange={(e) => setDeclarationAgreed(e.target.checked)} />
						I declare the above information is true and accurate.
					</label>

					<h2 style={{ marginTop: '30px' }}>Signature</h2>
					<p style={{ opacity: 0.7 }}>Client Name (Printed): {personal.fullName || '—'} &nbsp; Date: {moment().format('DD/MM/YYYY')}</p>
					<div style={{ border: '1px solid #c3c3c3', borderRadius: '4px', marginTop: '10px', width: '100%', maxWidth: '500px' }}>
						<SignatureCanvas
							ref={signatureRef}
							penColor="black"
							canvasProps={{ width: 500, height: 160, style: { width: '100%', height: '160px', touchAction: 'none' } }}
						/>
					</div>
					<button type="button" className="btn" style={{ width: 'auto', marginTop: '10px' }} onClick={() => signatureRef.current?.clear()}>
						Clear Signature
					</button>
					{signatureError && <p className={styles.fieldError} style={{ marginTop: '10px' }}>{signatureError}</p>}

					<div className={styles.actions}>
						<button type="submit" className="btn btnPrimary" disabled={loading}>
							{loading ? <Loading button={true} /> : `Submit & Continue`}
						</button>
					</div>

					{serverError &&
						<div className={styles.error} style={{ marginTop: '10px' }}>
							<p>{serverError}</p>
						</div>
					}
				</form>
			</div>
		</>
	);
}

export default HealthScreening;
